import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChevronUpDownIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import {
  type AdCreative,
  type AdCreativeApprovalStatus,
  useAdForgeApproveCreativeMutation,
  useAdForgeDeleteCreativeMutation,
  useAdForgeGetReviewLinkMutation,
  useAdForgeListCreativesQuery,
  useAdForgeRequestChangesCreativeMutation,
  useAdForgeSubmitCreativeMutation,
} from "~/ui-rtk/api/adForgeApi"
import {
  type MetaCreativeRow,
  type TikTokCreativeRow,
  useListMetaAdCreativesQuery,
  useListTikTokAdCreativesQuery,
} from "~/ui-rtk/api/paidMediaApi"
import { useGetCurrentAdAccountQuery } from "~/ui-rtk/api/campaignBlasterApi"
import { CONNECTORS_IDS } from "~/ui-rtk/constants/sources"
import { useCheckServiceIsConnected } from "~/ui-rtk/hooks/connected-services"
import { useAppSelector } from "~/ui-rtk/app/hooks"
// Direct import (not the `common` barrel) — the barrel pulls in InfoDialog →
// DateInput → dayjs side-effects which fail under our jest transform.
import Card from "~/ui-rtk/components/ui/common/Card"
import { Pagination } from "~/ui-rtk/components/ui/charts/TableVisualization/components/Pagination"
import { ITEMS_PER_PAGE } from "~/ui-rtk/components/ui/charts/TableVisualization/constants"
import Checkbox from "~/ui-rtk/components/ui/controls/Checkbox"
import Input from "~/ui-rtk/components/ui/controls/Input"
import Select from "~/ui-rtk/components/ui/controls/Select"
import { brandRoutes } from "~/ui-rtk/constants/routes"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import BlastTargetPickerModal, {
  type BlastSource,
} from "./BlastTargetPickerModal"

/**
 * Creatives tab — unified library. Renders AdForge drafts and the live Meta
 * AdCreative library in one table so users see every creative they could
 * blast from in a single view.
 *
 *   • Bulk-select rows with checkboxes (per-row + header "select all")
 *   • Sticky action bar appears when ≥1 row is selected: "Blast N creatives"
 *     → opens the shared target-picker modal (Phase 6), and "Delete N" for the
 *     AdForge drafts in the selection (Meta creatives live on Meta — no delete)
 *   • Source badge distinguishes DRAFT (built in AdForge) from META (lives on
 *     Meta only; would need an import step before blasting through our flow)
 *   • Client-side sort (click a column header: Name / Source / Status / CTA /
 *     Created) and filters (Source / Status / CTA selects). Both lists are
 *     fetched in full, so this runs over the merged rows before pagination.
 *
 * Phase 5d ships the unified table. The shared blast modal (Phase 6) will
 * handle each source's blast path differently.
 */

/* ----- Normalized row shape ----- */

type CreativeRowSource = "DRAFT" | "META" | "TIKTOK"

const SOURCE_LABEL: Record<CreativeRowSource, string> = {
  DRAFT: "AdForge",
  META: "Meta",
  TIKTOK: "TikTok",
}

interface UnifiedCreativeRow {
  /** Namespaced id: `draft:<uuid>`, `meta:<creative_id>` or `tiktok:<ad_id>`. The namespace prevents collision when sources happen to share a numeric-like id and lets the selection set carry mixed-source ids without ambiguity. */
  id: string
  source: CreativeRowSource
  name: string
  /** The preview URL itself — could be an image OR a video. The cell uses `previewType` to pick the right element. Meta `thumbnail_url` is always a still even when the underlying media is video, so for META rows this is always image. */
  previewUrl: string | null
  /** What `previewUrl` actually IS, so the preview cell can render `<img>` vs `<video>` correctly. Drafts that save an mp4 url without a separate thumbnail need `<video>` (which renders the first frame). */
  previewType: "image" | "video" | null
  /** Format only available for drafts; Meta library doesn't expose it directly. */
  format: AdCreative["format"] | null
  /** `_count.ads` for drafts; undefined for Meta rows (Meta's library doesn't carry usage stats inline). */
  launchedCount: number | null
  /** ISO timestamp for drafts; null for Meta rows (Meta's list endpoint doesn't ship a stable creation timestamp). */
  createdAt: string | null
  /** Underlying source id without the namespace prefix — used to look up the original record for actions. */
  rawId: string
  /** AdForge approval status (DRAFT rows only); null for Meta (already live). */
  approvalStatus: AdCreativeApprovalStatus | null
  /** Reviewer's note (what to change / why approved) — DRAFT rows only. */
  reviewNote: string | null
  /** Whether this row may be selected for a blast: Meta rows always; DRAFT rows only once APPROVED. */
  selectable: boolean
  /** Author of the draft — used to hide review actions on your own creative (you can't review your own). */
  createdById: string | null
  /** CTA enum (e.g. SHOP_NOW) — drafts carry it directly; Meta rows via the list endpoint. */
  cta: string | null
}

/**
 * Human label for a Meta CTA enum, e.g. `LEARN_MORE` → "Learn more". Meta
 * rows built from asset_feed_spec can carry several, joined with ", " —
 * each part is labeled on its own.
 */
function ctaLabel(cta: string): string {
  return cta
    .split(", ")
    .map(part => {
      const words = part.replace(/_/g, " ").toLowerCase()
      return words.charAt(0).toUpperCase() + words.slice(1)
    })
    .join(", ")
}

const FORMAT_LABEL: Record<AdCreative["format"], string> = {
  SINGLE_IMAGE: "Single image",
  SINGLE_VIDEO: "Single video",
  CAROUSEL: "Carousel",
}

function launchedCountFor(creative: AdCreative): number {
  // `_count.ads` is the Prisma include-count shape; bracket access sidesteps
  // no-underscore-dangle for this externally-defined key.
  return creative["_count"].ads
}

/**
 * Pick the URL + media type for the preview cell from a draft's first asset.
 * Prefer a still thumbnail when the asset shipped one — `<img>` renders are
 * lighter than spinning up a `<video>` element. Otherwise fall back to the
 * raw url and tell the cell what kind of media it is so it picks the right
 * tag (an mp4 url in an `<img>` would just render as a broken image, which
 * is the bug this paths around).
 */
function previewFor(creative: AdCreative): {
  url: string | null
  type: "image" | "video" | null
} {
  const first = creative.mediaAssets?.[0]
  if (!first) return { url: null, type: null }
  if (first.thumbnailUrl) return { url: first.thumbnailUrl, type: "image" }
  if (first.url) {
    return { url: first.url, type: first.type === "video" ? "video" : "image" }
  }
  return { url: null, type: null }
}

function draftToRow(c: AdCreative): UnifiedCreativeRow {
  const preview = previewFor(c)
  return {
    id: `draft:${c.id}`,
    rawId: c.id,
    source: "DRAFT",
    name: c.name,
    previewUrl: preview.url,
    previewType: preview.type,
    format: c.format,
    launchedCount: launchedCountFor(c),
    createdAt: c.createdAt,
    approvalStatus: c.approvalStatus,
    reviewNote: c.reviewNote,
    // Approval is advisory, not mandatory — any creative can be selected and
    // blasted regardless of its approval status.
    selectable: true,
    createdById: c.createdById,
    cta: c.callToAction || null,
  }
}

function metaToRow(c: MetaCreativeRow): UnifiedCreativeRow {
  // Meta's thumbnail_url is always a rendered still even when the underlying
  // media is a video — so this branch is always image.
  return {
    id: `meta:${c.id}`,
    rawId: c.id,
    source: "META",
    name: c.name ?? c.title ?? `Creative ${c.id}`,
    previewUrl: c.thumbnailUrl,
    previewType: c.thumbnailUrl ? "image" : null,
    format: null,
    launchedCount: null,
    createdAt: null,
    approvalStatus: null,
    reviewNote: null,
    selectable: true,
    createdById: null,
    cta: c.callToAction,
  }
}

function tiktokToRow(c: TikTokCreativeRow): UnifiedCreativeRow {
  // TikTok has no separate creative library — the ad IS the creative. The
  // thumbnail is a video cover / image still, so always an image.
  return {
    id: `tiktok:${c.id}`,
    rawId: c.id,
    source: "TIKTOK",
    name: c.name,
    previewUrl: c.thumbnailUrl,
    previewType: c.thumbnailUrl ? "image" : null,
    format: null,
    launchedCount: null,
    createdAt: c.createdAt,
    approvalStatus: null,
    reviewNote: null,
    selectable: true,
    createdById: null,
    cta: c.callToAction,
  }
}

/* ----- Inline cell components ----- */

interface PreviewCellProps {
  row: UnifiedCreativeRow
}
function PreviewCell({ row }: PreviewCellProps) {
  if (!row.previewUrl) {
    return <div className="h-10 w-10 rounded bg-basic-dark-blue" />
  }
  if (row.previewType === "video") {
    // `<video>` with `preload=metadata` renders the first frame as a poster
    // for free — no extra thumbnail fetch needed. `muted` keeps the browser
    // from rejecting autoplay (we're not autoplaying, but Safari is picky).
    return (
      <video
        src={row.previewUrl}
        muted
        preload="metadata"
        className="h-10 w-10 rounded object-cover"
        aria-label={row.name}
      />
    )
  }
  return (
    <img
      src={row.previewUrl}
      alt={row.name}
      className="h-10 w-10 rounded object-cover"
    />
  )
}

const APPROVAL_BADGE: Record<
  AdCreativeApprovalStatus,
  { label: string; cls: string }
> = {
  DRAFT: { label: "Draft", cls: "bg-system-gray-700 text-system-gray-200" },
  PENDING_APPROVAL: {
    label: "Pending approval",
    cls: "bg-system-yellow-700 text-white",
  },
  APPROVED: { label: "Approved", cls: "bg-system-green-700 text-white" },
  CHANGES_REQUESTED: {
    label: "Changes requested",
    cls: "bg-system-red-700 text-white",
  },
  REJECTED: { label: "Rejected", cls: "bg-system-red-900 text-white" },
}

interface StatusBadgeProps {
  row: UnifiedCreativeRow
}
function StatusBadge({ row }: StatusBadgeProps) {
  // META / TIKTOK rows just announce "Live on <platform>" — precise usage
  // stats would need an extra per-creative query we don't fan out for here.
  if (row.source !== "DRAFT") {
    return (
      <span className="rounded-full bg-system-blue-700 px-system-sm py-system-2xs text-xs font-medium text-white">
        Live on {SOURCE_LABEL[row.source]}
      </span>
    )
  }
  // DRAFT rows show their approval status (the primary signal) plus a launched
  // count when the creative has been blasted at least once.
  const approvalStatus = row.approvalStatus ?? "DRAFT"
  const badge = APPROVAL_BADGE[approvalStatus]
  const launched = row.launchedCount ?? 0
  // A creative with live ads isn't meaningfully a "Draft" any more — the
  // Launched badge replaces that label. Real review states (Approved,
  // Pending approval, Changes requested, Rejected) stay visible after
  // launch; only the default DRAFT label is suppressed.
  const showApprovalBadge = !(approvalStatus === "DRAFT" && launched > 0)
  // Surface the reviewer's note where the decision needs acting on — without
  // it "Changes requested" is a dead end (nowhere to see WHAT to change).
  const showNote =
    Boolean(row.reviewNote) &&
    (row.approvalStatus === "CHANGES_REQUESTED" ||
      row.approvalStatus === "REJECTED")
  return (
    <div className="flex max-w-56 flex-col gap-1">
      <div className="flex flex-wrap items-center gap-1">
        {showApprovalBadge && (
          <span
            className={cn(
              "rounded-full px-system-sm py-system-2xs text-xs font-medium",
              badge.cls,
            )}
            title={row.reviewNote ?? undefined}
          >
            {badge.label}
          </span>
        )}
        {launched > 0 && (
          <span className="rounded-full bg-system-blue-700 px-system-sm py-system-2xs text-xs font-medium text-white">
            Launched ({launched})
          </span>
        )}
      </div>
      {showNote && (
        <p
          className="line-clamp-2 text-xs italic text-system-gray-400"
          title={row.reviewNote ?? undefined}
        >
          “{row.reviewNote}”
        </p>
      )}
    </div>
  )
}

interface SourceBadgeProps {
  source: CreativeRowSource
}
function SourceBadge({ source }: SourceBadgeProps) {
  return (
    <span
      className={cn(
        "rounded-full px-system-sm py-system-2xs text-xs font-medium text-white",
        source === "DRAFT" && "bg-system-purple-700",
        source === "META" && "bg-system-blue-700",
        source === "TIKTOK" && "bg-basic-black ring-1 ring-gray-500",
      )}
    >
      {SOURCE_LABEL[source]}
    </span>
  )
}

/* ----- Sorting + filtering ----- */

type SortKey = "name" | "source" | "status" | "cta" | "createdAt"
type SortDir = "asc" | "desc"
interface SortState {
  key: SortKey
  dir: SortDir
}

// First click on a header sorts in the direction people expect for that
// column: newest first for Created, A→Z for everything else.
const DEFAULT_SORT_DIR: Record<SortKey, SortDir> = {
  name: "asc",
  source: "asc",
  status: "asc",
  cta: "asc",
  createdAt: "desc",
}

// Status sort order follows the review pipeline: least → most ready to blast,
// with Meta's already-live creatives at the end.
const STATUS_RANK: Record<AdCreativeApprovalStatus, number> = {
  DRAFT: 0,
  CHANGES_REQUESTED: 1,
  PENDING_APPROVAL: 2,
  REJECTED: 3,
  APPROVED: 4,
}
const META_STATUS_RANK = 5

/**
 * The value a row sorts on for a given key. `null` means "no value" (Meta
 * rows have no createdAt, many rows have no CTA) — those always sink to the
 * bottom regardless of direction, so flipping asc/desc never buries the rows
 * that actually have data under a wall of dashes.
 */
function sortValue(
  row: UnifiedCreativeRow,
  key: SortKey,
): string | number | null {
  switch (key) {
    case "name":
      return row.name.toLowerCase()
    case "source":
      return SOURCE_LABEL[row.source].toLowerCase()
    case "status":
      return row.source !== "DRAFT"
        ? META_STATUS_RANK
        : STATUS_RANK[row.approvalStatus ?? "DRAFT"]
    case "cta":
      return row.cta ? ctaLabel(row.cta).toLowerCase() : null
    case "createdAt":
      return row.createdAt ? Date.parse(row.createdAt) : null
  }
}

function sortRows(
  rows: UnifiedCreativeRow[],
  sort: SortState | null,
): UnifiedCreativeRow[] {
  if (!sort) return rows
  const withValue: Array<{ row: UnifiedCreativeRow; v: string | number }> = []
  const without: UnifiedCreativeRow[] = []
  for (const row of rows) {
    const v = sortValue(row, sort.key)
    if (v === null) without.push(row)
    else withValue.push({ row, v })
  }
  const sign = sort.dir === "asc" ? 1 : -1
  withValue.sort((a, b) => {
    if (a.v < b.v) return -sign
    if (a.v > b.v) return sign
    return 0
  })
  return [...withValue.map(x => x.row), ...without]
}

type SourceFilter = "ALL" | CreativeRowSource
type StatusFilter =
  | "ALL"
  | AdCreativeApprovalStatus
  | "LAUNCHED"
  | "LIVE_ON_META"
  | "LIVE_ON_TIKTOK"
/** `ALL`, `NONE` (rows without a CTA), or a raw CTA enum present in the data. */
type CtaFilter = string

const SOURCE_FILTER_OPTIONS: Array<{ label: string; value: SourceFilter }> = [
  { label: "All sources", value: "ALL" },
  { label: "AdForge", value: "DRAFT" },
  { label: "Meta", value: "META" },
  { label: "TikTok", value: "TIKTOK" },
]

const STATUS_FILTER_OPTIONS: Array<{ label: string; value: StatusFilter }> = [
  { label: "All statuses", value: "ALL" },
  { label: APPROVAL_BADGE.DRAFT.label, value: "DRAFT" },
  { label: APPROVAL_BADGE.PENDING_APPROVAL.label, value: "PENDING_APPROVAL" },
  { label: APPROVAL_BADGE.APPROVED.label, value: "APPROVED" },
  {
    label: APPROVAL_BADGE.CHANGES_REQUESTED.label,
    value: "CHANGES_REQUESTED",
  },
  { label: APPROVAL_BADGE.REJECTED.label, value: "REJECTED" },
  { label: "Launched", value: "LAUNCHED" },
  { label: "Live on Meta", value: "LIVE_ON_META" },
  { label: "Live on TikTok", value: "LIVE_ON_TIKTOK" },
]

function matchesStatus(row: UnifiedCreativeRow, filter: StatusFilter): boolean {
  switch (filter) {
    case "ALL":
      return true
    case "LIVE_ON_META":
      return row.source === "META"
    case "LIVE_ON_TIKTOK":
      return row.source === "TIKTOK"
    case "LAUNCHED":
      // Orthogonal to approval status — a launched creative keeps its review
      // state, so this matches on the launched count alone.
      return (row.launchedCount ?? 0) > 0
    default:
      return (
        row.source === "DRAFT" && (row.approvalStatus ?? "DRAFT") === filter
      )
  }
}

function matchesCta(row: UnifiedCreativeRow, filter: CtaFilter): boolean {
  if (filter === "ALL") return true
  if (filter === "NONE") return !row.cta
  return row.cta === filter
}

interface SortableHeaderProps {
  label: string
  sortKey: SortKey
  sort: SortState | null
  onToggle: (key: SortKey) => void
  className?: string
}
function SortableHeader({
  label,
  sortKey,
  sort,
  onToggle,
  className,
}: SortableHeaderProps) {
  const dir = sort?.key === sortKey ? sort.dir : null
  const SORT_ICON = { asc: ArrowUpIcon, desc: ArrowDownIcon } as const
  const Icon = dir ? SORT_ICON[dir] : ChevronUpDownIcon
  const ariaSort = { asc: "ascending", desc: "descending" } as const
  return (
    <th
      aria-sort={dir ? ariaSort[dir] : "none"}
      className={cn(
        "px-2 py-1.5 border-s border-s-basic-blue bg-basic-dark-blue text-md text-white font-bold text-left",
        className,
      )}
    >
      <button
        type="button"
        onClick={() => onToggle(sortKey)}
        aria-label={`Sort by ${label}`}
        title={`Sort by ${label}`}
        className={cn(
          "inline-flex items-center gap-1 whitespace-nowrap hover:text-brand",
          dir && "text-brand",
        )}
      >
        {label}
        <Icon className="h-3.5 w-3.5" />
      </button>
    </th>
  )
}

interface BulkActionBarProps {
  selectedCount: number
  /** How many of the selected rows are AdForge drafts — the only source that can be deleted. 0 hides the delete button (e.g. a Meta-only selection). */
  deletableCount: number
  onBlast: () => void
  onDelete: () => void
  onClear: () => void
}
function BulkActionBar({
  selectedCount,
  deletableCount,
  onBlast,
  onDelete,
  onClear,
}: BulkActionBarProps) {
  // Reserve the row even when empty so the table doesn't jump as selections
  // come and go. Phase 5b accepts the small visual cost in exchange for the
  // stability — Phase 6's modal will be the more prominent action surface.
  if (selectedCount === 0) {
    return (
      <p className="text-system-gray-300 text-sm">
        Select creatives to blast them to ad sets.
      </p>
    )
  }
  return (
    <div className="flex items-center justify-between rounded-md border border-system-blue-600 bg-system-blue-700 px-system-md py-system-sm">
      <span className="text-sm text-white">
        {selectedCount} creative{selectedCount === 1 ? "" : "s"} selected
      </span>
      <div className="flex items-center gap-system-sm">
        <button
          type="button"
          onClick={onClear}
          className="text-sm text-system-gray-200 hover:text-white"
        >
          Clear
        </button>
        {deletableCount > 0 && (
          <button
            type="button"
            onClick={onDelete}
            title="Deletes the selected AdForge drafts. Meta creatives live on Meta and can't be deleted from here."
            className="rounded-md bg-system-red-700 px-system-md py-system-2xs text-sm font-medium text-white hover:bg-system-red-600"
          >
            Delete {deletableCount}
          </button>
        )}
        <button
          type="button"
          onClick={onBlast}
          className="rounded-md bg-system-green-600 px-system-md py-system-2xs text-sm font-medium text-white hover:bg-system-green-500"
        >
          Blast {selectedCount}
        </button>
      </div>
    </div>
  )
}

interface ConfirmDeleteDialogProps {
  /** The drafts queued for deletion (null = dialog closed). */
  targets: Array<{ rawId: string; name: string }> | null
  deleting: boolean
  onClose: () => void
  onConfirm: () => void
}
/**
 * Confirmation before deleting drafts — deletion is permanent, so it never
 * fires straight off a row click. Mirrors the raw-overlay pattern of
 * RequestChangesDialog (kept local for the same jest-transform reason).
 */
function ConfirmDeleteDialog({
  targets,
  deleting,
  onClose,
  onConfirm,
}: ConfirmDeleteDialogProps) {
  if (!targets) return null
  const count = targets.length
  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm"
      onClick={deleting ? undefined : onClose}
    >
      <div
        className="w-full max-w-lg rounded-xl border border-system-gray-800 bg-system-gray-900 p-5"
        onClick={e => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-white">
          Delete {count === 1 ? "creative" : `${count} creatives`}
        </h2>
        {deleting ? (
          // Swap the whole body for an unmissable in-flight state — a tiny
          // button spinner alone reads as "nothing is happening" on slow
          // connections or large bulk deletes.
          <div className="mt-4 flex items-center gap-3 rounded-md bg-system-gray-800/60 px-system-md py-system-sm text-sm text-system-gray-200">
            <span className="loading loading-spinner loading-md text-system-red-500" />
            Deleting {count === 1 ? "creative" : `${count} creatives`}… Hang
            tight, this closes on its own.
          </div>
        ) : (
          <>
            <p className="mt-1 text-sm text-system-gray-400">
              {count === 1 ? (
                <>
                  Delete{" "}
                  <span className="font-medium text-white">
                    {targets[0].name}
                  </span>
                  ?
                </>
              ) : (
                <>Delete these {count} creatives?</>
              )}{" "}
              This can&apos;t be undone. Ads already launched from{" "}
              {count === 1 ? "it" : "them"} stay live — they just stop linking
              back to the draft.
            </p>
            {count > 1 && (
              <ul className="mt-3 max-h-40 list-inside list-disc overflow-y-auto text-sm text-system-gray-300">
                {targets.map(t => (
                  <li key={t.rawId} className="truncate">
                    {t.name}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={deleting}
            className="btn btn-ghost btn-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={deleting}
            className="btn btn-error btn-sm"
          >
            {deleting && (
              <span className="loading loading-spinner loading-xs" />
            )}
            {deleting ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  )
}

interface RequestChangesDialogProps {
  target: { id: string; name: string } | null
  onClose: () => void
  onSubmit: (note: string) => void
}
/**
 * Lightweight modal for sending a creative back with a note. Mirrors the
 * raw-overlay pattern used by ScrapeFromUrlDialog (kept local — it's only used
 * here). The note is optional; an empty note still moves the creative to
 * CHANGES_REQUESTED.
 */
function RequestChangesDialog({
  target,
  onClose,
  onSubmit,
}: RequestChangesDialogProps) {
  const [note, setNote] = useState("")
  if (!target) return null
  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-xl border border-system-gray-800 bg-system-gray-900 p-5"
        onClick={e => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-white">Request changes</h2>
        <p className="mt-1 text-sm text-system-gray-400">
          Send <span className="font-medium text-white">{target.name}</span>{" "}
          back to the author with a note on what to change.
        </p>
        <textarea
          value={note}
          onChange={e => setNote(e.target.value)}
          rows={4}
          autoFocus
          placeholder="What needs to change? (optional)"
          className="textarea textarea-bordered mt-3 w-full"
        />
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-ghost btn-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSubmit(note)}
            className="btn btn-primary btn-sm"
          >
            Request changes
          </button>
        </div>
      </div>
    </div>
  )
}

export default function CreativesTab() {
  const navigate = useNavigate()
  // Current user — a creative can't be reviewed by its own author (the backend
  // returns 403), so we hide the review actions on your own drafts.
  const currentUserId = useAppSelector(state => state.userSlice.user.id)
  // Search input value (every keystroke) + the debounced value we actually
  // hand to the queries. Without the debounce we'd fire one HTTP request per
  // typed character; 300ms is long enough to skip mid-word firings without
  // feeling laggy.
  const [searchInput, setSearchInput] = useState("")
  const [searchQuery, setSearchQuery] = useState("")
  useEffect(() => {
    const trimmed = searchInput.trim()
    const timer = setTimeout(() => setSearchQuery(trimmed), 300)
    return () => clearTimeout(timer)
  }, [searchInput])

  const queryArg = searchQuery ? { search: searchQuery } : undefined

  const {
    data: drafts,
    isLoading: draftsLoading,
    isFetching: draftsFetching,
    error: draftsError,
  } = useAdForgeListCreativesQuery(queryArg)
  // The Meta library needs a Meta ad account. A company without one (e.g.
  // TikTok-only) would otherwise hit a hard error on every load and see a red
  // "couldn't fetch" banner for a platform it never connected — skip the
  // query instead and say so quietly.
  const { data: metaAccount, isLoading: metaAccountLoading } =
    useGetCurrentAdAccountQuery()
  const hasMetaAccount = !!metaAccount?.adAccountId
  const {
    data: metaCreatives,
    isLoading: metaLoading,
    isFetching: metaFetching,
    error: metaError,
  } = useListMetaAdCreativesQuery(queryArg, { skip: !hasMetaAccount })
  const metaLibraryUnavailable = !metaAccountLoading && !hasMetaAccount

  // TikTok source: the advertiser's ads (TikTok has no separate creative
  // library). Only fetched when TikTok Ads Management is connected — the
  // same connector the re-blast path authenticates with.
  const isTikTokConnected = useCheckServiceIsConnected(
    CONNECTORS_IDS.TIKTOK_ADS_MANAGEMENT,
  )
  const {
    data: tiktokCreatives,
    isLoading: tiktokLoading,
    isFetching: tiktokFetching,
    error: tiktokError,
  } = useListTikTokAdCreativesQuery(queryArg, { skip: !isTikTokConnected })

  // Local selection state — clears on navigate away, which is the desired
  // behavior. Selection ids carry their source prefix (`draft:` / `meta:`)
  // so downstream the blast handler can route each one correctly.
  const [selected, setSelected] = useState<Set<string>>(new Set())

  const rows = useMemo<UnifiedCreativeRow[]>(() => {
    const draftRows = (drafts ?? []).map(draftToRow)
    const metaRows = (metaCreatives ?? []).map(metaToRow)
    const tiktokRows = (tiktokCreatives ?? []).map(tiktokToRow)
    // Drafts first — most recent-feeling content the user just built. Live
    // platform creatives trail because they're typically a longer
    // historical tail.
    return [...draftRows, ...metaRows, ...tiktokRows]
  }, [drafts, metaCreatives, tiktokCreatives])

  // ----- Sort + filter (client-side over the merged rows) -----
  // `null` sort keeps the server order (drafts newest-first, then Meta).
  const [sort, setSort] = useState<SortState | null>(null)
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>("ALL")
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("ALL")
  const [ctaFilter, setCtaFilter] = useState<CtaFilter>("ALL")

  // Header click cycles: unsorted → column default direction → the opposite
  // → unsorted. Clicking a different column starts that column's cycle.
  const toggleSort = useCallback((key: SortKey) => {
    setSort(prev => {
      if (!prev || prev.key !== key) return { key, dir: DEFAULT_SORT_DIR[key] }
      if (prev.dir === DEFAULT_SORT_DIR[key]) {
        return { key, dir: prev.dir === "asc" ? "desc" : "asc" }
      }
      return null
    })
  }, [])

  // CTA options come from the data itself — the enum is wide and mostly
  // unused, so listing only what's present keeps the dropdown short.
  const ctaFilterOptions = useMemo(() => {
    const present = new Map<string, string>()
    let hasNone = false
    for (const row of rows) {
      if (row.cta) present.set(row.cta, ctaLabel(row.cta))
      else hasNone = true
    }
    const options = [...present.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label))
    return [
      { value: "ALL", label: "All CTAs" },
      ...(hasNone ? [{ value: "NONE", label: "No CTA" }] : []),
      ...options,
    ]
  }, [rows])

  const hasActiveFilters =
    sourceFilter !== "ALL" || statusFilter !== "ALL" || ctaFilter !== "ALL"

  const clearFilters = useCallback(() => {
    setSourceFilter("ALL")
    setStatusFilter("ALL")
    setCtaFilter("ALL")
  }, [])

  const visibleRows = useMemo(() => {
    const filtered = rows.filter(
      r =>
        (sourceFilter === "ALL" || r.source === sourceFilter) &&
        matchesStatus(r, statusFilter) &&
        matchesCta(r, ctaFilter),
    )
    return sortRows(filtered, sort)
  }, [rows, sourceFilter, statusFilter, ctaFilter, sort])

  // Client-side filters hide rows without touching `rows`, so a selection
  // made before filtering would otherwise keep feeding hidden drafts into
  // Delete / Blast (both resolve ids against the full `rows`). Keep the
  // selection scoped to what the user can currently see — this also covers a
  // server-side search shrinking the list.
  useEffect(() => {
    setSelected(prev => {
      if (prev.size === 0) return prev
      const visible = new Set(visibleRows.map(r => r.id))
      const next = new Set([...prev].filter(id => visible.has(id)))
      return next.size === prev.size ? prev : next
    })
  }, [visibleRows])

  // Pagination state mirrors @tanstack/react-table's PaginationState shape so
  // we can drop in the shared <Pagination /> from TableVisualization without
  // an adapter.
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: ITEMS_PER_PAGE,
  })

  // Search / filter / sort changes the row order or count — reset to page 0
  // so the user doesn't land on an empty (or reshuffled) page mid-change.
  // Same for when the underlying data refetches and shrinks below the
  // current page boundary.
  useEffect(() => {
    setPagination(prev =>
      prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 },
    )
  }, [searchQuery, sourceFilter, statusFilter, ctaFilter, sort])

  const totalPages = Math.max(
    1,
    Math.ceil(visibleRows.length / pagination.pageSize),
  )

  // Clamp pageIndex if pagination state lags behind a filter shrink.
  const safePageIndex = Math.min(pagination.pageIndex, totalPages - 1)

  const paginatedRows = useMemo(() => {
    const start = safePageIndex * pagination.pageSize
    return visibleRows.slice(start, start + pagination.pageSize)
  }, [visibleRows, safePageIndex, pagination.pageSize])

  // Select-all checkbox operates on the CURRENTLY VISIBLE, SELECTABLE rows
  // (post-filter AND post-pagination) — clicking it under an active search/page
  // shouldn't sweep in rows the user can't see, and unapproved drafts aren't
  // selectable (they can't be blasted yet).
  const allIds = useMemo(
    () => paginatedRows.filter(r => r.selectable).map(r => r.id),
    [paginatedRows],
  )
  const allSelected = allIds.length > 0 && allIds.every(id => selected.has(id))
  const someSelected = selected.size > 0 && !allSelected

  const toggleRow = useCallback((id: string) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  // Toggle every row currently visible on the page. The check is
  // "every page row already in the set?", not "selection count equals page
  // size?" — the latter is wrong cross-page: 25 page-1 rows selected, user
  // moves to page 2 (also 25 rows), header checkbox click would mistake
  // `prev.size === allIds.length` for "all of page 2 already selected" and
  // wipe the page-1 selection. Matches the predicate `allSelected` uses
  // upstream so the header checkbox and this handler agree.
  const toggleAll = useCallback(() => {
    setSelected(prev => {
      const allOnPage = allIds.every(id => prev.has(id))
      const next = new Set(prev)
      if (allOnPage) {
        for (const id of allIds) next.delete(id)
      } else {
        for (const id of allIds) next.add(id)
      }
      return next
    })
  }, [allIds])

  const clearSelection = useCallback(() => setSelected(new Set()), [])

  const [blastModalOpen, setBlastModalOpen] = useState(false)
  const handleBlast = useCallback(() => setBlastModalOpen(true), [])

  // ----- Approval workflow -----
  const [submitCreative] = useAdForgeSubmitCreativeMutation()
  const [approveCreative] = useAdForgeApproveCreativeMutation()
  const [requestChangesCreative] = useAdForgeRequestChangesCreativeMutation()
  const [getReviewLink] = useAdForgeGetReviewLinkMutation()
  // The creative whose review link is currently being minted — drives the
  // per-row spinner and disables that button so a double-click can't fire two
  // token requests.
  const [copyingLinkId, setCopyingLinkId] = useState<string | null>(null)

  const handleCopyReviewLink = useCallback(
    async (id: string) => {
      if (copyingLinkId) return
      setCopyingLinkId(id)
      try {
        const { token } = await getReviewLink(id).unwrap()
        const url = `${window.location.origin}/review/${token}`
        await navigator.clipboard.writeText(url)
        toast.success("Review link copied — anyone with it can review.")
      } catch {
        toast.error("Couldn't create a review link")
      } finally {
        setCopyingLinkId(null)
      }
    },
    [getReviewLink, copyingLinkId],
  )
  // The creative whose "Request changes" dialog is open (null = closed).
  const [reviewTarget, setReviewTarget] = useState<{
    id: string
    name: string
  } | null>(null)

  // The creative whose approval action (submit / approve) is in flight — disables
  // that row's action buttons so a double-click can't fire duplicate requests.
  const [busyRowId, setBusyRowId] = useState<string | null>(null)

  // ----- Delete -----
  const [deleteCreative] = useAdForgeDeleteCreativeMutation()
  // Drafts queued for deletion, pending the confirm dialog (null = closed).
  // Single-row delete and bulk delete share this — the row action queues one
  // entry, the bulk button queues every selected draft.
  const [deleteTargets, setDeleteTargets] = useState<Array<{
    rawId: string
    name: string
  }> | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // Only AdForge drafts can be deleted — Meta rows live on Meta.
  const selectedDraftRows = useMemo(
    () => rows.filter(r => r.source === "DRAFT" && selected.has(r.id)),
    [rows, selected],
  )

  // Raw draft ids currently being deleted (or deleted and awaiting the list
  // refetch). Drives the per-row loading state: dimmed row + spinner in the
  // Actions cell. Successful ids stay in the set until the refetched list no
  // longer contains them — without that, the row would "come back to life"
  // for the gap between the DELETE resolving and the list refetch landing.
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set())

  useEffect(() => {
    setDeletingIds(prev => {
      if (prev.size === 0) return prev
      const alive = new Set((drafts ?? []).map(d => d.id))
      const next = new Set([...prev].filter(id => alive.has(id)))
      return next.size === prev.size ? prev : next
    })
  }, [drafts])

  const handleBulkDelete = useCallback(() => {
    // Skip rows that already have a delete in flight so re-opening the dialog
    // mid-delete can't double-fire on the same draft.
    const targets = selectedDraftRows.filter(r => !deletingIds.has(r.rawId))
    if (targets.length === 0) return
    setDeleteTargets(targets.map(r => ({ rawId: r.rawId, name: r.name })))
  }, [selectedDraftRows, deletingIds])

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTargets || isDeleting) return
    setIsDeleting(true)
    setDeletingIds(prev => {
      const next = new Set(prev)
      for (const t of deleteTargets) next.add(t.rawId)
      return next
    })
    // Fire all deletes and settle each independently — one 4xx/5xx shouldn't
    // abort the rest of a bulk delete.
    const results = await Promise.allSettled(
      deleteTargets.map(t => deleteCreative(t.rawId).unwrap()),
    )
    const deletedIds = deleteTargets
      .filter((_, i) => results[i].status === "fulfilled")
      .map(t => t.rawId)
    const failedIds = deleteTargets
      .filter((_, i) => results[i].status === "rejected")
      .map(t => t.rawId)
    if (deletedIds.length > 0) {
      // Drop deleted rows from the selection so the blast bar count doesn't
      // keep counting creatives that no longer exist.
      setSelected(prev => {
        const next = new Set(prev)
        for (const id of deletedIds) next.delete(`draft:${id}`)
        return next
      })
      toast.success(
        deletedIds.length === 1
          ? "Creative deleted"
          : `${deletedIds.length} creatives deleted`,
      )
    }
    if (failedIds.length > 0) {
      // Failed rows stay in the list — release their loading state so their
      // actions work again. Successful ids are pruned by the drafts effect.
      setDeletingIds(prev => {
        const next = new Set(prev)
        for (const id of failedIds) next.delete(id)
        return next
      })
      toast.error(
        failedIds.length === 1
          ? "Couldn't delete 1 creative"
          : `Couldn't delete ${failedIds.length} creatives`,
      )
    }
    setIsDeleting(false)
    setDeleteTargets(null)
  }, [deleteTargets, isDeleting, deleteCreative])

  const handleSubmitForApproval = useCallback(
    async (id: string) => {
      if (busyRowId) return
      setBusyRowId(id)
      try {
        await submitCreative(id).unwrap()
        toast.success("Submitted for approval")
      } catch {
        toast.error("Couldn't submit this creative")
      } finally {
        setBusyRowId(null)
      }
    },
    [submitCreative, busyRowId],
  )

  const handleApprove = useCallback(
    async (id: string) => {
      if (busyRowId) return
      setBusyRowId(id)
      try {
        await approveCreative({ id }).unwrap()
        toast.success("Creative approved")
      } catch {
        toast.error("Couldn't approve this creative")
      } finally {
        setBusyRowId(null)
      }
    },
    [approveCreative, busyRowId],
  )

  const handleRequestChanges = useCallback(
    async (note: string) => {
      if (!reviewTarget) return
      try {
        await requestChangesCreative({
          id: reviewTarget.id,
          note: note.trim() || undefined,
        }).unwrap()
        toast.success("Changes requested")
        setReviewTarget(null)
      } catch {
        toast.error("Couldn't request changes")
      }
    },
    [requestChangesCreative, reviewTarget],
  )

  // Resolve the selected ids back to row metadata + the underlying source
  // record. The id prefix (`draft:` / `meta:`) controls which blast endpoint
  // each row hits; the underlying record gives us copy/CTA the modal renders
  // in its preview strip so users can sanity-check before launching.
  const blastSources = useMemo<BlastSource[]>(() => {
    const rowById = new Map(rows.map(r => [r.id, r]))
    const draftById = new Map((drafts ?? []).map(d => [d.id, d]))
    const metaById = new Map((metaCreatives ?? []).map(m => [m.id, m]))
    const tiktokById = new Map((tiktokCreatives ?? []).map(t => [t.id, t]))
    const list: BlastSource[] = []
    for (const id of selected) {
      const row = rowById.get(id)
      if (!row) continue
      if (row.source === "TIKTOK") {
        // A TikTok row is a launched ad: it re-blasts through the same
        // `ad` unit the Ads tab uses (same-advertiser clone of the source
        // ad's processed video), so the modal offers TikTok ad groups.
        const t = tiktokById.get(row.rawId)
        list.push({
          kind: "ad",
          id: row.rawId,
          name: row.name,
          platform: "tiktok",
          externalId: row.rawId,
          previewUrl: row.previewUrl,
          previewType: row.previewType,
          primaryText: t?.primaryText ?? null,
          headline: null,
          callToAction: t?.callToAction ?? null,
        })
      } else if (row.source === "DRAFT") {
        const d = draftById.get(row.rawId)
        list.push({
          kind: "draft",
          id: row.rawId,
          name: row.name,
          previewUrl: row.previewUrl,
          previewType: row.previewType,
          primaryText: d?.primaryText ?? null,
          headline: d?.headline ?? null,
          callToAction: d?.callToAction ?? null,
          // Drives the modal's required-tags check — the ads this draft
          // launches inherit exactly these tags.
          tags: d?.tags ?? null,
          // Drives the modal's TikTok gate (TikTok ads are video-only).
          // Undefined when the draft record isn't loaded — the server-side
          // check in prepareBlastAdCreative still applies.
          hasVideoAsset: d
            ? (d.mediaAssets ?? []).some(a => a.type === "video")
            : undefined,
        })
      } else {
        const m = metaById.get(row.rawId)
        list.push({
          kind: "meta",
          id: row.rawId,
          name: row.name,
          previewUrl: row.previewUrl,
          previewType: row.previewType,
          // Meta's `body` is the primary text; `title` is the headline. The
          // modal handles both naming families uniformly.
          primaryText: m?.body ?? null,
          headline: m?.title ?? null,
          callToAction: null,
        })
      }
    }
    return list
  }, [rows, selected, drafts, metaCreatives, tiktokCreatives])

  // Loading: show the spinner while ANY source is loading on first paint.
  // Once one source resolves, render what we have and let the others stream in.
  const initialLoading =
    (draftsLoading && !drafts) ||
    (metaLoading && !metaCreatives) ||
    (tiktokLoading && !tiktokCreatives)
  if (initialLoading) {
    return (
      <div className="p-system-md text-system-gray-300">Loading creatives…</div>
    )
  }

  // Empty-state copy: filters hiding everything reads differently from a
  // search with no hits, which reads differently from an empty library.
  let emptyStateCopy =
    "No creatives yet. Build one in AdForge or run a Meta ad to see it here."
  if (rows.length > 0) {
    emptyStateCopy = "No creatives match the current filters."
  } else if (searchInput) {
    emptyStateCopy = `No creatives match "${searchInput}".`
  }

  // Hard fail only if BOTH sources errored — a partial failure still gives
  // the user something useful (and is surfaced inline below).
  if (draftsError && metaError) {
    return (
      <div className="p-system-md text-system-red-500">
        Failed to load creatives. Check the Meta connector status and refresh.
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-system-sm">
      <div className="flex flex-wrap items-center gap-system-sm">
        <Input
          fullWidth
          type="search"
          aria-label="Search creatives"
          placeholder={
            isTikTokConnected
              ? "Search saved drafts, your Meta library and TikTok ads…"
              : "Search saved drafts and your Meta library…"
          }
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          prependNode={
            <MagnifyingGlassIcon className="h-4 w-4 text-system-gray-300" />
          }
          className="min-w-64 flex-1"
        />
        <Select
          aria-label="Filter by source"
          options={SOURCE_FILTER_OPTIONS}
          value={sourceFilter}
          onChange={e => setSourceFilter(e.target.value as SourceFilter)}
          className="h-9 w-40 text-sm"
        />
        <Select
          aria-label="Filter by status"
          options={STATUS_FILTER_OPTIONS}
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value as StatusFilter)}
          className="h-9 w-44 text-sm"
        />
        <Select
          aria-label="Filter by CTA"
          options={ctaFilterOptions}
          value={ctaFilter}
          onChange={e => setCtaFilter(e.target.value)}
          className="h-9 w-40 text-sm"
        />
        {hasActiveFilters && (
          <button
            type="button"
            onClick={clearFilters}
            className="text-xs font-medium text-system-gray-300 hover:text-white whitespace-nowrap"
          >
            Clear filters
          </button>
        )}
        {(searchInput || hasActiveFilters) && (
          <span className="text-xs text-system-gray-300 whitespace-nowrap">
            {/* Any source's `isFetching` reflects an in-flight search even
             * when stale data is still shown — useful signal during the 300ms
             * debounce window. */}
            {draftsFetching || metaFetching || tiktokFetching
              ? "Searching…"
              : `${visibleRows.length} result${visibleRows.length === 1 ? "" : "s"}`}
          </span>
        )}
      </div>

      <BulkActionBar
        selectedCount={selected.size}
        deletableCount={selectedDraftRows.length}
        onBlast={handleBlast}
        onDelete={handleBulkDelete}
        onClear={clearSelection}
      />

      {metaError && !draftsError && (
        <div className="rounded-md border border-system-red-600 bg-system-red-900/40 px-system-md py-system-sm text-xs text-system-red-200">
          Couldn&apos;t fetch your Meta library. Showing AdForge drafts only.
        </div>
      )}
      {metaLibraryUnavailable && !draftsError && (
        <p className="text-xs text-gray-400">
          No Meta ad account is configured for this company, so the Meta
          creative library isn&apos;t shown.
        </p>
      )}
      {tiktokError && !draftsError && (
        <div className="rounded-md border border-system-red-600 bg-system-red-900/40 px-system-md py-system-sm text-xs text-system-red-200">
          Couldn&apos;t fetch your TikTok ads. Check the TikTok Ads Management
          connector and the advertiser account in company settings.
        </div>
      )}
      {draftsError && !metaError && (
        <div className="rounded-md border border-system-red-600 bg-system-red-900/40 px-system-md py-system-sm text-xs text-system-red-200">
          Couldn&apos;t load saved drafts. Showing Meta library only.
        </div>
      )}

      <Card
        rounded
        className="border border-basic-blue bg-basic-dark-blue space-y-3"
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="w-full border-b border-b-basic-blue">
              <tr>
                <th className="w-10 px-2 py-1.5 border-s-basic-blue bg-basic-dark-blue text-md text-white font-bold">
                  <Checkbox
                    checked={allSelected}
                    className={cn(someSelected && "opacity-60")}
                    onChange={toggleAll}
                  />
                </th>
                <th className="w-16 px-2 py-1.5 border-s border-s-basic-blue bg-basic-dark-blue text-md text-white font-bold text-left">
                  Preview
                </th>
                <SortableHeader
                  label="Name"
                  sortKey="name"
                  sort={sort}
                  onToggle={toggleSort}
                  className="w-[415px] min-w-[415px] max-w-[415px]"
                />
                <SortableHeader
                  label="Source"
                  sortKey="source"
                  sort={sort}
                  onToggle={toggleSort}
                />
                <SortableHeader
                  label="Status"
                  sortKey="status"
                  sort={sort}
                  onToggle={toggleSort}
                />
                <SortableHeader
                  label="CTA"
                  sortKey="cta"
                  sort={sort}
                  onToggle={toggleSort}
                />
                <SortableHeader
                  label="Created"
                  sortKey="createdAt"
                  sort={sort}
                  onToggle={toggleSort}
                />
                <th className="px-2 py-1.5 border-s border-s-basic-blue bg-basic-dark-blue text-md text-white font-bold text-left">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="w-full">
              {paginatedRows.map(row => {
                const isSelected = selected.has(row.id)
                // Delete in flight (or done, awaiting the refetch that drops
                // the row) — dim the row and freeze its controls.
                const isRowDeleting =
                  row.source === "DRAFT" && deletingIds.has(row.rawId)
                return (
                  <tr
                    key={row.id}
                    className={cn(
                      "border-b border-b-basic-blue bg-basic-black hover:bg-basic-dark-blue/70",
                      isSelected && "bg-basic-dark-blue/50",
                      isRowDeleting && "opacity-50",
                    )}
                  >
                    <td className="px-2 py-1.5 text-sm">
                      <span
                        title={
                          row.selectable
                            ? undefined
                            : "Approve this creative before blasting"
                        }
                      >
                        <Checkbox
                          checked={isSelected}
                          disabled={!row.selectable || isRowDeleting}
                          onChange={() => toggleRow(row.id)}
                        />
                      </span>
                    </td>
                    <td className="px-2 py-1.5 border-s border-s-basic-blue text-sm">
                      <PreviewCell row={row} />
                    </td>
                    {/* Fixed 415px column: the cell is capped and the text
                     * truncates, so a long name can't stretch the table. */}
                    <td className="w-[415px] min-w-[415px] max-w-[415px] overflow-hidden px-2 py-1.5 border-s border-s-basic-blue text-sm">
                      <div className="flex min-w-0 flex-col">
                        <span
                          className="truncate font-medium text-white"
                          title={row.name}
                        >
                          {row.name}
                        </span>
                        <span className="truncate text-xs text-gray-400">
                          {row.format
                            ? FORMAT_LABEL[row.format]
                            : `ID: ${row.rawId}`}
                        </span>
                      </div>
                    </td>
                    <td className="px-2 py-1.5 border-s border-s-basic-blue text-sm">
                      <SourceBadge source={row.source} />
                    </td>
                    <td className="px-2 py-1.5 border-s border-s-basic-blue text-sm">
                      <StatusBadge row={row} />
                    </td>
                    <td
                      className="px-2 py-1.5 border-s border-s-basic-blue whitespace-nowrap text-xs text-gray-400"
                      title={row.cta ?? undefined}
                    >
                      {row.cta ? ctaLabel(row.cta) : "—"}
                    </td>
                    <td className="px-2 py-1.5 border-s border-s-basic-blue whitespace-nowrap text-xs text-gray-400">
                      {row.createdAt
                        ? new Date(row.createdAt).toLocaleDateString()
                        : "—"}
                    </td>
                    <td className="px-2 py-1.5 border-s border-s-basic-blue text-sm">
                      {isRowDeleting && (
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-system-red-500">
                          <span className="loading loading-spinner loading-xs" />
                          Deleting…
                        </span>
                      )}
                      {!isRowDeleting && row.source === "DRAFT" && (
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                          <button
                            type="button"
                            onClick={() =>
                              navigate(
                                `${brandRoutes.AD_BLASTER.AD_FORGE}?creative=${row.rawId}`,
                              )
                            }
                            className="text-xs font-medium text-brand hover:underline"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleCopyReviewLink(row.rawId)}
                            disabled={copyingLinkId === row.rawId}
                            className="inline-flex items-center gap-1 text-xs font-medium text-system-blue-300 hover:underline disabled:opacity-60"
                          >
                            {copyingLinkId === row.rawId && (
                              <span className="loading loading-spinner loading-xs" />
                            )}
                            {copyingLinkId === row.rawId
                              ? "Copying…"
                              : "Copy review link"}
                          </button>
                          {(row.approvalStatus === "DRAFT" ||
                            row.approvalStatus === "CHANGES_REQUESTED") && (
                            <button
                              type="button"
                              onClick={() => handleSubmitForApproval(row.rawId)}
                              disabled={busyRowId === row.rawId}
                              className="inline-flex items-center gap-1 text-xs font-medium text-system-blue-300 hover:underline disabled:opacity-60"
                            >
                              {busyRowId === row.rawId && (
                                <span className="loading loading-spinner loading-xs" />
                              )}
                              Submit for approval
                            </button>
                          )}
                          {row.approvalStatus === "PENDING_APPROVAL" &&
                            // You can't review your own creative — show the
                            // actions only to a different user (matches the
                            // backend's enforceDistinctReviewer guard).
                            (row.createdById &&
                            row.createdById === currentUserId ? (
                              <span className="text-xs text-system-gray-400">
                                Awaiting review
                              </span>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  onClick={() => handleApprove(row.rawId)}
                                  disabled={busyRowId === row.rawId}
                                  className="inline-flex items-center gap-1 text-xs font-medium text-system-green-500 hover:underline disabled:opacity-60"
                                >
                                  {busyRowId === row.rawId && (
                                    <span className="loading loading-spinner loading-xs" />
                                  )}
                                  Approve
                                </button>
                                <button
                                  type="button"
                                  disabled={busyRowId === row.rawId}
                                  onClick={() =>
                                    setReviewTarget({
                                      id: row.rawId,
                                      name: row.name,
                                    })
                                  }
                                  className="text-xs font-medium text-system-red-500 hover:underline disabled:opacity-60"
                                >
                                  Request changes
                                </button>
                              </>
                            ))}
                          <button
                            type="button"
                            onClick={() =>
                              setDeleteTargets([
                                { rawId: row.rawId, name: row.name },
                              ])
                            }
                            disabled={busyRowId === row.rawId}
                            className="text-xs font-medium text-system-red-500 hover:underline disabled:opacity-60"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                      {!isRowDeleting && row.source !== "DRAFT" && (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                )
              })}
              {visibleRows.length === 0 && (
                <tr>
                  <td
                    colSpan={8}
                    className="px-2 py-system-lg text-center text-sm text-gray-400 bg-basic-black"
                  >
                    {emptyStateCopy}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {visibleRows.length > 0 && (
          <div className="px-2 pb-2">
            <Pagination
              state={{
                pageIndex: safePageIndex,
                pageSize: pagination.pageSize,
              }}
              total={totalPages}
              onNext={() =>
                setPagination(prev => ({
                  ...prev,
                  pageIndex: Math.min(prev.pageIndex + 1, totalPages - 1),
                }))
              }
              onPrevious={() =>
                setPagination(prev => ({
                  ...prev,
                  pageIndex: Math.max(prev.pageIndex - 1, 0),
                }))
              }
              onChangeState={partial =>
                setPagination(prev => ({ ...prev, ...partial }))
              }
            />
          </div>
        )}
      </Card>

      <BlastTargetPickerModal
        open={blastModalOpen}
        onClose={() => setBlastModalOpen(false)}
        sources={blastSources}
      />

      <RequestChangesDialog
        key={reviewTarget?.id ?? "closed"}
        target={reviewTarget}
        onClose={() => setReviewTarget(null)}
        onSubmit={handleRequestChanges}
      />

      <ConfirmDeleteDialog
        targets={deleteTargets}
        deleting={isDeleting}
        onClose={() => setDeleteTargets(null)}
        onConfirm={handleConfirmDelete}
      />
    </div>
  )
}
